import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
import { configureAmplify } from './config/amplify'
import { AuthProvider } from './contexts/auth-context'
import { isNativeApp } from './utils/platform'
import { registerNativeOAuthHandlers } from './utils/native-oauth'
import { registerNativeCallDeepLinkHandlers } from './utils/native-call-deeplink'
import { LoadingSplashProvider } from './contexts/loading-splash-context'
import { MessagingMobileNavProvider } from './contexts/messaging-mobile-nav-context'
import { VoiceCallProvider } from './contexts/voice-call-context'
import { OnboardingTourProvider } from './features/onboarding-tour/tour-context'
import { AccessibilityProvider } from './features/accessibility/accessibility-context'
import { initCallFlowLog } from './debug/call-flow-log'
import { registerNativeSessionRecordingAdapters } from './features/session-recording/data/register-native-session-recording'

configureAmplify()

// Live call telemetry: start the buffer, prime the device id + auth token, and
// wire the native bridge so structured call events ship to `recordCallTelemetry`.
// Guarded and fire-and-forget — never affects the call path.
initCallFlowLog()

if (isNativeApp()) {
  registerNativeOAuthHandlers()
  registerNativeCallDeepLinkHandlers()
  registerNativeSessionRecordingAdapters()
}

if (!isNativeApp() && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        registration.update().catch(() => {})
        setInterval(() => registration.update().catch(() => {}), 60 * 60 * 1000)
      })
      .catch(() => {
        /* non-fatal — app works without offline shell */
      })
  })
}

// Opt-in simulator preview build: boot straight into the no-auth chrome preview.
if (import.meta.env.VITE_SAFE_AREA_PREVIEW === 'true') {
  const query = import.meta.env.VITE_SAFE_AREA_PREVIEW_QUERY ?? 'view=chat'
  window.history.replaceState(null, '', `/dev/safe-area-preview?${query}`)
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <LoadingSplashProvider>
          <MessagingMobileNavProvider>
            <VoiceCallProvider>
              <AccessibilityProvider>
                <OnboardingTourProvider>
                  <div className="native-viewport flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden">
                    <App />
                  </div>
                </OnboardingTourProvider>
              </AccessibilityProvider>
            </VoiceCallProvider>
          </MessagingMobileNavProvider>
        </LoadingSplashProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
